// Display only: Shopify and the existing cart integrations remain purchase authorities.
export function purchaseTotal({ parentPrice, additions = [], quantity = 1, complete = true }) {
  if (!Number.isSafeInteger(quantity) || quantity < 1 || !Array.isArray(additions)) return null;
  const amounts = additions.map((value) => Number.isSafeInteger(value) ? value : value?.priceCents);
  if (![parentPrice, ...amounts].every((value) => Number.isSafeInteger(value) && value >= 0)) return null;
  const unitTotal = amounts.reduce((sum, value) => sum + value, parentPrice);
  const total = unitTotal * quantity;
  if (!Number.isSafeInteger(unitTotal) || !Number.isSafeInteger(total)) return null;
  return { unitTotal, total, complete, hasAdditions: amounts.length > 0 };
}

// Same rendered-field contract as the deployed Product Hub selected-price reader.
export function readArisPrice(container) {
  const additions = [];
  let complete = true;
  for (const required of container.querySelectorAll('[apo-required="true"]')) {
    if (required.closest('[disabled]')) continue;
    const completed = Array.from(required.querySelectorAll('input, select, textarea')).some((control) => {
      if (control.disabled || control.closest('[disabled]') || ['hidden', 'file'].includes(control.type)) return false;
      return ['radio', 'checkbox'].includes(control.type) ? control.checked : Boolean(control.value?.trim());
    });
    if (!completed) complete = false;
  }
  for (const control of container.querySelectorAll('input[field-name], select[field-name], textarea[field-name]')) {
    if (control.disabled || control.closest('[disabled]') ||
      (['radio', 'checkbox'].includes(control.type) && !control.checked)) continue;
    if (['hidden', 'file'].includes(control.type)) return { additions: [NaN], complete: false };
    const option = control.tagName === 'SELECT' ? control.selectedOptions[0] : control;
    if (!option) { complete = false; continue; }
    const raw = option.getAttribute('data-variant-price');
    if (option.getAttribute('data-variant-id') && (raw === null || raw === '')) return { additions: [NaN], complete: false };
    const price = raw === null || raw === '' ? 0 : Number(raw);
    if (option.getAttribute('data-variant-onetime') === 'true') return { additions: [NaN], complete: false };
    if (option.getAttribute('data-variant-id')) {
      additions.push({ variantId: String(option.getAttribute('data-variant-id')), priceCents: price });
    } else if (price !== 0) additions.push(price);
  }
  return { additions, complete };
}

const bound = new WeakSet();
const writeText = (node, value) => { if (node && node.textContent !== value) node.textContent = value; };
export function bindPurchasePrice(dataNode) {
  let data;
  try { data = JSON.parse(dataNode.textContent); } catch (_error) { return; }
  const form = document.getElementById(`product_form_${data.productId}`);
  const details = form?.closest('.product-details');
  const card = details?.querySelector('[data-acqua-pdp-base-price]');
  if (!form || !card || bound.has(form) || !Array.isArray(data.variants)) return;
  bound.add(form);
  const springRoot = details.querySelector('[data-acqua-spring-pricing]');
  const money = (cents) => new Intl.NumberFormat('en-AU', { style: 'currency', currency: data.currency }).format(cents / 100);
  const note = document.createElement('p');
  note.className = 'acqua-pdp-purchase-note';
  note.hidden = true;
  note.setAttribute('role', 'status');
  note.setAttribute('aria-live', 'polite');
  (form.querySelector('.product-form--atc') || form).append(note);
  let hub = { additions: [], complete: true };
  let queued = false;
  const render = () => {
    queued = false;
    const id = form.querySelector('[name="id"]')?.value ||
      form.querySelector('[data-variant-selection]')?.getAttribute('variant') || data.selectedVariantId;
    const variant = data.variants.find((item) => String(item.id) === String(id));
    if (!variant) return;
    const offer = springRoot && typeof window.AcquaSpringPricing?.update === 'function'
      ? window.AcquaSpringPricing.update(springRoot, String(id)) : null;
    card.hidden = Boolean(offer);
    const reference = Number.isSafeInteger(variant.compare_at_price) && variant.compare_at_price > variant.price
      ? variant.compare_at_price : null;
    writeText(card.querySelector('.acq-retail-price-card__price'), money(variant.price));
    writeText(card.querySelector('[data-acqua-base-heading]'), reference ? data.everydaySavings : data.everydayPrice);
    const comparison = card.querySelector('.acq-retail-price-card__meta');
    if (comparison) {
      comparison.hidden = !reference;
      const values = comparison.querySelectorAll('strong');
      if (reference) {
        writeText(values[0], money(reference));
        writeText(values[1], `${Math.round((reference - variant.price) * 100 / reference)}%`);
      }
    }
    writeText(card.querySelector('.acq-retail-price-card__payments strong'), money(variant.price / 4));
    const aris = form.querySelector('.avpoptions-container__v2');
    const options = aris ? readArisPrice(aris) : hub;
    const result = purchaseTotal({
      parentPrice: offer?.salePriceCents ?? variant.price,
      ...options,
      quantity: Number(form.querySelector('[name="quantity"]')?.value || 1),
    });
    const button = form.querySelector('[data-product-atc]');
    if (!button) return;
    let amount = button.querySelector('[data-acqua-purchase-total]');
    if (!amount) {
      amount = document.createElement('span');
      amount.className = 'acqua-pdp-purchase-total';
      amount.setAttribute('data-acqua-purchase-total', '');
      button.append(amount);
    }
    amount.hidden = !result || variant.available === false;
    writeText(amount, result ? money(result.total) : '');
    const estimated = Boolean(aris && result?.hasAdditions);
    writeText(note, !result ? data.unavailable
      : !result.complete ? ''
        : estimated ? data.estimated : '');
    note.hidden = !note.textContent || variant.available === false;
    details.setAttribute('data-acqua-purchase-pricing', '');
    details.toggleAttribute('data-acqua-purchase-total-valid', Boolean(result) && variant.available !== false);
  };
  const schedule = () => { if (!queued) { queued = true; queueMicrotask(render); } };
  form.addEventListener('input', schedule);
  form.addEventListener('change', schedule);
  form.addEventListener('click', (event) => {
    if (event.target.closest?.('[data-quantity-plus], [data-quantity-minus]')) schedule();
  });
  form.addEventListener('variant-change', schedule, true);
  document.addEventListener('acqua:spring-pricing-change', (event) => {
    if (event.detail?.root === springRoot) schedule();
  });
  document.addEventListener('acqua:configuration-price', (event) => {
    if (String(event.detail?.productId) !== String(data.productId) || event.detail?.owner !== 'product-hub') return;
    hub = { additions: Array.isArray(event.detail.additions) ? event.detail.additions : [NaN], complete: true };
    schedule();
  });
  new MutationObserver(schedule).observe(form, {
    childList: true, subtree: true, attributes: true,
    attributeFilter: ['disabled', 'variant', 'data-variant-price', 'data-variant-onetime'],
  });
  render();
  document.dispatchEvent(new CustomEvent('acqua:configuration-price-request', { detail: { productId: data.productId } }));
}

if (typeof document !== 'undefined') {
  const init = () => document.querySelectorAll('[data-acqua-pdp-purchase-data]').forEach(bindPurchasePrice);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
  document.addEventListener('shopify:section:load', init);
}
